/**
 * Globe Switcher — State machine managing Earth ↔ Moon transitions.
 *
 * Transition sequence (Earth → Moon):
 *   0ms      — Star field appears, Earth begins fading out
 *   1200ms   — Earth fully faded, stars visible ("traveling through space")
 *   1800ms   — Moon begins fading in over the stars
 *   3000ms   — Moon fully visible, star field fades away
 *   3400ms   — Transition complete, Moon interactive
 *
 * Dispatches DOM events:
 *   'globeswitch:start'    — { to: 'moon' | 'earth' }
 *   'globeswitch:complete' — { mode: 'moon' | 'earth' }
 */
import {
  initMoonGlobe,
  isInitialized,
  startAutoRotate as startMoonRotate,
  stopAutoRotate as stopMoonRotate,
} from './moon-globe.js';

let currentMode = 'earth';
let isTransitioning = false;

let earthContainer = null;
let moonContainer = null;
let earthGlobe = null;

function init(globe) {
  earthGlobe = globe;
  earthContainer = document.getElementById('globe-container');
  moonContainer = document.getElementById('moon-globe-container');
}

/**
 * Switch from Earth to Moon.
 */
async function switchToMoon() {
  if (currentMode === 'moon' || isTransitioning) return;
  isTransitioning = true;

  document.dispatchEvent(
    new CustomEvent('globeswitch:start', { detail: { to: 'moon' } })
  );

  // Lazy-init Moon globe on first click
  if (!isInitialized()) {
    moonContainer.classList.remove('moon-hidden');
    moonContainer.style.display = 'block';
    moonContainer.style.opacity = '0';
    await initMoonGlobe();
  } else {
    moonContainer.classList.remove('moon-hidden');
    moonContainer.style.display = 'block';
    moonContainer.style.opacity = '0';
  }

  earthGlobe.stopAutoRotate();

  // Phase 1: Show stars + fade Earth out (0 → 1200ms)
  document.body.classList.add('star-field');
  earthContainer.style.transition = 'opacity 1.2s ease-in';
  void earthContainer.offsetHeight;
  earthContainer.style.opacity = '0';

  // Phase 2: Brief star field pause (1200 → 1800ms) — "traveling through space"
  setTimeout(() => {
    earthContainer.style.display = 'none';

    // Phase 3: Fade Moon in over the stars (1800 → 3000ms)
    moonContainer.style.transition = 'opacity 1.2s ease-out';
    void moonContainer.offsetHeight;
    moonContainer.style.opacity = '1';

    // Phase 4: Finalize — keep stars visible behind the Moon
    setTimeout(() => {
      // Clean up transition styles
      earthContainer.style.transition = '';
      moonContainer.style.transition = '';

      currentMode = 'moon';
      isTransitioning = false;

      startMoonRotate();

      document.dispatchEvent(
        new CustomEvent('globeswitch:complete', { detail: { mode: 'moon' } })
      );
    }, 1400);
  }, 1400);
}

/**
 * Switch from Moon back to Earth.
 */
async function switchToEarth() {
  if (currentMode === 'earth' || isTransitioning) return;
  isTransitioning = true;

  document.dispatchEvent(
    new CustomEvent('globeswitch:start', { detail: { to: 'earth' } })
  );

  stopMoonRotate();

  // Phase 1: Show stars + fade Moon out
  document.body.classList.add('star-field');
  moonContainer.style.transition = 'opacity 1.2s ease-in';
  void moonContainer.offsetHeight;
  moonContainer.style.opacity = '0';

  // Phase 2: Brief star field pause
  setTimeout(() => {
    moonContainer.style.display = 'none';
    moonContainer.classList.add('moon-hidden');

    // Re-show Earth (transparent) then fade in
    earthContainer.style.display = 'block';
    earthContainer.style.opacity = '0';
    earthContainer.style.transition = 'opacity 1.2s ease-out';
    void earthContainer.offsetHeight;
    earthContainer.style.opacity = '1';

    // Phase 3: Finalize — remove stars once Earth is fully back
    setTimeout(() => {
      document.body.classList.remove('star-field');

      earthContainer.style.transition = '';
      earthContainer.style.opacity = '';
      moonContainer.style.transition = '';
      moonContainer.style.opacity = '';

      currentMode = 'earth';
      isTransitioning = false;

      earthGlobe.startAutoRotate();

      document.dispatchEvent(
        new CustomEvent('globeswitch:complete', { detail: { mode: 'earth' } })
      );
    }, 1400);
  }, 1400);
}

function getCurrentMode() {
  return currentMode;
}

function isInTransition() {
  return isTransitioning;
}

export default { init, switchToMoon, switchToEarth, getCurrentMode, isInTransition };
